import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { InferType } from 'yup';
import { toast } from 'sonner';

import { useAuthStore } from '@/features/auth/auth.store';
import type { IImage } from '@/types/image';

import {
  clientCreateValidationSchema,
  clientValidationSchema,
} from '../../console.validations';
import type { Client } from '../../console.types';
import { ClientsService } from './clients.service';
import { getPageSize } from '@/stores/page-size.store';
import { useUrlFilters } from '@/hooks/use-url-filters';

/** Only this role may reach `/company` at all — see ClientsService. */
const SUPER_ADMIN_ROLE = 'Admin';

/**
 * How many tenants the sidebar switcher lists at once. The endpoint does the
 * matching, so this is a page size rather than a cap on what is reachable:
 * anything past it is found by typing, not by scrolling.
 */
const OPTIONS_LIMIT = 5;

type ClientFormValues = InferType<typeof clientValidationSchema>;
type ClientCreateValues = InferType<typeof clientCreateValidationSchema>;

/** `undefined` for an empty/whitespace-only input, so the key is left out. */
const trimmed = (value?: string) => {
  const next = value?.trim();
  return next ? next : undefined;
};

/** The stored filename of the dropzone's single file, if it holds one. */
const logoName = (logo?: IImage[]) => logo?.[0]?.name || undefined;

/**
 * Builds the POST body, leaving out optionals the operator did not fill in.
 * Written field by field rather than filtered generically so `name` stays
 * required — the API rejects a body without it.
 */
const toCreatePayload = (values: ClientCreateValues): Client.ICreateClient => ({
  name: values.name.trim(),
  address: trimmed(values.address),
  phone: trimmed(values.phone),
  senderEmail: trimmed(values.senderEmail),
  senderName: trimmed(values.senderName),
  contactEmail: trimmed(values.contactEmail),
  replyToEmail: trimmed(values.replyToEmail),
  // Stored by the API on the new company_detail, not on the company.
  logo: logoName(values.logo),
});

/**
 * Builds the PUT body. `address` and `phone` are sent even when emptied, so
 * clearing them sticks; the email fields are dropped when blank, because
 * the API validates them with @IsEmail and rejects an empty string. Clearing
 * an address works, then — clearing an email does not.
 */
const toUpdatePayload = (values: ClientFormValues): Client.IUpdateClient => ({
  name: values.name.trim(),
  address: values.address?.trim() ?? '',
  phone: values.phone?.trim() ?? '',
  senderEmail: trimmed(values.senderEmail),
  // Sent even when emptied, like `address`: it is not an email, so '' passes
  // validation, and clearing it puts the sender back on the client name.
  senderName: values.senderName?.trim() ?? '',
  contactEmail: trimmed(values.contactEmail),
  replyToEmail: trimmed(values.replyToEmail),
  twoFactorEnabled: values.twoFactorEnabled,
  isBetaTester: values.isBetaTester,
});

/** True when the signed-in user may list and create companies. */
const useIsSuperAdmin = () =>
  useAuthStore(state => state.user?.role?.name) === SUPER_ADMIN_ROLE;

/**
 * Every company the signed-in user may switch between, unpaginated. Used by
 * the sidebar company selector; the list page keeps using `useClientList`.
 *
 * Skipped entirely for anyone but a super Admin: the endpoint would 403, and
 * nobody else has a company to switch to.
 */
const useCompanyOptions = (search?: string) => {
  const enabled = useIsSuperAdmin();

  // Left off the key entirely when empty, so browsing always hits the same
  // cache entry rather than one keyed to an empty string.
  const term = search?.trim() ? search.trim() : undefined;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      'company-options',
      { page: 1, limit: OPTIONS_LIMIT, ...(term ? { search: term } : {}) },
    ],
    queryFn: ClientsService.getClients,
    // `total` is what the registry holds — or, while searching, how many match.
    // Either way it is not the length of this page, which is what the switcher
    // needs to say how many tenants it is not showing.
    select: response => ({
      companies: response.data ?? [],
      total: Number(response.meta?.total_data ?? response.data?.length ?? 0),
    }),
    enabled,
    // Keeps the previous matches on screen while the next ones load, so the
    // list does not blink empty between keystrokes.
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: data?.companies ?? [],
    total: data?.total ?? 0,
    isLoading,
    isSearching: isFetching,
  };
};

/**
 * Paginated client list. Search, sort and paging all happen server-side; the
 * filter state below is only what gets sent as query params.
 */
const useClientList = () => {
  const initialFilters: Client.IClientFilters = {
    search: '',
    page: 1,
    limit: getPageSize('console.client'),
    sort: '',
    order: '',
  };

  const [filters, setFilters] =
    useUrlFilters<Client.IClientFilters>(initialFilters);

  const { data, isLoading } = useQuery({
    queryKey: ['clients', filters],
    queryFn: ClientsService.getClients,
  });

  const resetFilters = () => setFilters(initialFilters);

  return {
    filters,
    setFilters,
    resetFilters,
    data: {
      data: data?.data ?? [],
      meta: {
        page: Number(data?.meta?.page ?? filters.page ?? 1),
        limit: Number(data?.meta?.limit ?? filters.limit ?? 10),
        total_data: data?.meta?.total_data ?? 0,
        total_pages: data?.meta?.total_pages ?? 1,
      },
    },
    isLoading,
  };
};

/** Form state for the "Add Clients" dialog. */
const useClientForm = (onCreated?: (client?: Client.IClient) => void) => {
  const queryClient = useQueryClient();

  const form = useForm({
    resolver: yupResolver(clientCreateValidationSchema),
    defaultValues: {
      // `[]`, not undefined, so removing an upload returns to pristine.
      logo: [] as IImage[],
      name: '',
      address: '',
      phone: '',
      senderEmail: '',
      senderName: '',
      contactEmail: '',
      replyToEmail: '',
    },
    mode: 'onChange',
  });

  const mutation = useMutation({
    mutationFn: ClientsService.createClient,
  });

  // An untouched optional input is '', and the API's @IsOptional only skips
  // undefined — an empty string still reaches @IsEmail and comes back a 400.
  // So drop the blanks rather than sending them.
  const onSubmit = (values: ClientCreateValues) =>
    mutation.mutate(toCreatePayload(values), {
      onSuccess: response => {
        // Both the table and the sidebar switcher read the same companies.
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        queryClient.invalidateQueries({ queryKey: ['company-options'] });
        form.reset();
        toast.success(response?.meta?.message ?? `${values.name} added`);
        onCreated?.(response?.data);
      },
      onError: (err: { message?: string }) =>
        toast.error(err?.message ?? 'Something went wrong'),
    });

  return { form, onSubmit, isSaving: mutation.isPending };
};

// Deliberately unannotated: plain values let useForm infer the same shape as
// the "Add client" defaults, which the yup resolver accepts.
const toEditValues = (client?: Client.IClient) => ({
  name: client?.name ?? '',
  address: client?.address ?? '',
  phone: client?.phone ?? '',
  senderEmail: client?.senderEmail ?? '',
  senderName: client?.senderName ?? '',
  contactEmail: client?.contactEmail ?? '',
  replyToEmail: client?.replyToEmail ?? '',
  twoFactorEnabled: client?.twoFactorEnabled ?? false,
  isBetaTester: client?.isBetaTester ?? false,
});

/**
 * Form state for editing a client in place on the Detail tab.
 *
 * The row the modal was opened with is a snapshot held by the list page, so a
 * saved client is handed back through `onSaved` — invalidating the list alone
 * would refresh the table behind the modal but leave the modal itself showing
 * the values the operator just replaced.
 */
const useClientEdit = (
  client?: Client.IClient,
  onSaved?: (client?: Client.IClient) => void
) => {
  const queryClient = useQueryClient();

  const form = useForm({
    resolver: yupResolver(clientValidationSchema),
    defaultValues: toEditValues(client),
    mode: 'onChange',
  });

  const mutation = useMutation({
    mutationFn: ClientsService.updateClient,
  });

  // Follow the client the modal is showing — the operator can pick another one
  // without the modal unmounting.
  useEffect(() => {
    form.reset(toEditValues(client));
  }, [client, form]);

  const onSubmit = (values: ClientFormValues) => {
    if (!client?.id) return;

    mutation.mutate(
      { id: client.id, payload: toUpdatePayload(values) },
      {
        onSuccess: response => {
          queryClient.invalidateQueries({ queryKey: ['clients'] });
          queryClient.invalidateQueries({ queryKey: ['company-options'] });
          // The Feature flags page shows the beta tester badge and filter.
          queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
          // Mark pristine straight away so Save/Reset disable without waiting
          // for the refetch.
          form.reset(values);
          toast.success(response?.meta?.message ?? 'Client updated');
          onSaved?.(response?.data);
        },
        onError: (err: { message?: string }) =>
          toast.error(err?.message ?? 'Something went wrong'),
      }
    );
  };

  /** Back to the last-saved values, staying in edit mode. */
  const resetChanges = () => form.reset(toEditValues(client));

  return {
    form,
    onSubmit,
    resetChanges,
    isDirty: form.formState.isDirty,
    isSaving: mutation.isPending,
  };
};

export {
  useClientList,
  useClientForm,
  useClientEdit,
  useCompanyOptions,
  useIsSuperAdmin,
};
